import React, { useState, useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Container, Typography, Grid, Button, RadioButton, Dropdown, LoadingIndicators } from '../../canvasComponents/index';
import '../../css/domain/Services/Services.scss';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import messages from './messages';
import introIllustration from '../../img/services/vendor-locator-into.svg';
import CustomAutocompleteDMTI from '../../FormioComponents/Custom/utils/AutocompleteDMTI/AutocompleteDMTI';
import { audatargetSelector } from '../../redux/Services/selectors';
import { searchAudatargetVendor, audatargetGetAvailibility } from '../../redux/Services/actions';
import { addressValidation } from '../../services/addressService';
import NoLongerAvailablePopUp from './NoLongerAvailablePopUp';
import { getRouteUrl } from '../../FormioComponents/utils';
import useLeavingPageHandler from '../../hooks/useLeavingPageHandler';
import { checkFive9Availibility } from '../../services/five9Service';

function VendorLocator({ onFinalContinue, saveAndContinueLater, updateClaim, audatargetClaimId, gwclaimId, serviceData, policyAddress, vehicle, audatargetLocations, lastStep, updateCurrentPage }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  // Vendor Locator steps
  const [step, setStep] = useState(lastStep && lastStep !== 'connectWithUs' ? lastStep : 'cerfLanding');
  // Selected branch from radio list
  const [vendorSelection, setVendorSelection] = useState(serviceData?.vendorLocation || null);
  // Required vendor selection error
  const [requiredError, setRequiredError] = useState(false);
  // Required address error
  const [requiredErrorAddress, setRequiredErrorAddress] = useState(false);
  // Selected address object from the address typeahead input field
  const [addressInput, setAddressInput] = useState({ value: serviceData?.searchedLocation || policyAddress, addressObj: {} });
  const [userLocation, setUserLocation] = useState({});
  // enterprise locations object
  const [vendorLocatorSearchLocations, setVendorLocatorSearchLocations] = useState([]);
  // days for the next 2 weeks
  const [selectDayOptions, setSelectDayOptions] = useState([]);
  // selected days
  const [selectedDay, setSelectedDay] = useState(serviceData?.day);
  // time options
  const [selectTimeOptions, setSelectTimeOptions] = useState([]);
  // selected day for appointment
  const [selectedTime, setSelectedTime] = useState(serviceData?.time);
  // selected estimator Id
  const [estimatorId, setEstimatorId] = useState(serviceData?.estimatorId);
  // selected time slot is no longer available
  const [noLongerAvailable, setNoLongerAvailable] = useState(false);
  // integration error happened
  const [errorHappened, setErrorHappened] = useState(false);
  // data to save to DB
  const [dbData, setDbData] = useState({ ...serviceData });
  const [isUpdateRequired, setIsUpdateRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isLeavingSoSoonPopUpOpen } = useLeavingPageHandler('Vehicle', document.querySelector('.services-heading')?.textContent, true);

  const getDmtiAddress = async () => {
    try {
      const address = await addressValidation(addressInput?.value);
      if (address.addressValidation?.addressValidationResult === 'true') {
        const addressObj = {
          street: address.addressValidation.streetNumber,
          streetName: address.addressValidation.streetName,
          city: address.addressValidation.municipality,
          country: 'CA',
          provinceState: address.addressValidation.province,
          postalCode: address.addressValidation.postalCode,
          latitude: address.addressValidation.latitude,
          longitude: address.addressValidation.longitude,
        };
        setUserLocation({ value: addressInput?.value, addressObj });
      }
      return null;
    } catch (error) {
      console.log('TODO CALL ERROR SERVICE');
    }
  };

  useEffect(() => {
    window.history.pushState({}, window.location.pathname, `#/${getRouteUrl(document.querySelector('.services-heading')?.textContent, 'Vehicle') || ''}`);
  }, [step]);

  useEffect(() => {
    if (userLocation && userLocation.value) {
      const { addressObj } = userLocation;
      const addressLine1 = addressObj.street || addressObj.streetName ? `${addressObj.street || ''}${addressObj.streetName ? ` ${addressObj.streetName}` : ''}` : 'Unknown';
      const postalCode = addressObj.postalCode || 'X0X 0X0';
      const vendorSchemeName = vehicle.make != null && vehicle.make == 'TESLA' ? 'OE Cert (Tesla)' : null; 
      const address = {
        addressLine1,
        city: addressObj.city,
        postalCode,
        locality: '',
        province: addressObj.provinceState,
        country: 'CA',
        latitude: addressObj.latitude,
        longitude: addressObj.longitude,
      };
      const searchCriteria = {
        claimId: audatargetClaimId,
        schemeName: vendorSchemeName,
        address,
      };
      dispatch(searchAudatargetVendor(searchCriteria));
      setDbData({ ...dbData, searchedLocation: userLocation.value });
    }
  }, [userLocation]);

  useEffect(() => {
    if (isLeavingSoSoonPopUpOpen) {
      document.getElementById('leave-popup-button').addEventListener('click', () => {
        saveAndContinueLater('audatarget', step, dbData);
      });
    }
  }, [isLeavingSoSoonPopUpOpen]);

  useEffect(() => {
    const availabilities = audatargetLocations?.availability?.data;
    if (step === 'appointmentDetails') {
      if (availabilities && availabilities.length && availabilities[0].date === selectedDay) {
        const dayTimeSlots = availabilities[0].estimators?.length && availabilities[0].estimators[0].timeSlots;
        if (dayTimeSlots.find((slot) => slot.timeFrom === selectedTime.timeFrom && slot.isAvailable)) {
          setDbData({ ...dbData, isAudatargetVisible: true, triggerAudatarget: !!vendorSelection && selectedDay && selectedTime });
          setIsUpdateRequired(true);
          onFinalContinue({
            isAudatargetVisible: true,
            triggerAudatarget: !!vendorSelection && selectedDay && selectedTime,
            repairShop: vendorSelection ? vendorSelection?.name : <FormattedMessage id="Claims Professional to help arrange" defaultMessage="Claims team member to help arrange" />,
            address: vendorSelection?.address,
            appointmentDay: selectedDay,
            appointmentTime: selectedTime,
            appointmentAvailability: bookAppointment(),
            convertToTimeString: (time) => convertToTimeString(time),
            previousStep: () => onClickContinue('selectRepairShop'),
          });
        } else {
          setNoLongerAvailable(true);
        }
      }
    } else {
      const next2Weeks = [];
      // create selectDay Options - next 14 days
      if (availabilities && availabilities.length) {
        for (let i = 0; i < availabilities.length; i += 1) {
          const day = availabilities[i];
          if (day.estimators.length && day.estimators[0].timeSlots.find((timeSlot) => timeSlot.isAvailable)) {
            next2Weeks.push({
              label: convertToDateString(day.date),
              value: day.date,
              selectedLabel: convertToDateString(day.date),
            });
          }
        }
      }
      setSelectDayOptions(next2Weeks);
    }
  }, [audatargetLocations.availability?.data]);

  useEffect(() => {
    const vendorLocation = audatargetLocations?.searchVendor?.data ? audatargetLocations.searchVendor.data.map((item) => ({
      entityId: item.id,
      name: item.name,
      address: item.address,
      mapAddress: `https://www.google.com/maps/search/?api=1&query=${item.address.addressLine1}, ${item.address.province} ${item.address.postalCode}`,
      number: item.telephoneNumber,
      email: item.emailAddress,
      economicalADPCode: item.customFields.find((c) => c.name === 'Economical ID')?.values?.[0],
      sonnetADPCode: item.customFields.find((c) => c.name === 'Sonnet ID')?.values?.[0],
    })) : [];
    setVendorLocatorSearchLocations(vendorLocation);
  }, [audatargetLocations.searchVendor]);

  useEffect(() => {
    if (audatargetLocations.searchVendor?.error?.error) {
      setErrorHappened(true);
    } else if (errorHappened) {
      onFinalContinue({
        isAudatargetVisible: true,
        triggerAudatarget: false,
        repairShop: <FormattedMessage id="Claims Professional to help arrange" defaultMessage="Claims team member to help arrange" />,
      });
      setDbData({ ...dbData, isAudatargetVisible: true, triggerAudatarget: false });
    }
  }, [audatargetLocations.searchVendor.error]);

  const getLocations = (searchedAddress) => {
    setVendorLocatorSearchLocations([]);
    setAddressInput(searchedAddress);
    setUserLocation(searchedAddress);
  };

  const selectDateAvailibility = (date) => {
    setSelectTimeOptions([]);

    const day = audatargetLocations.availability.data?.filter((d) => d.date === date);
    if (day?.length) {
      const estimator = day[0].estimators[0];
      setEstimatorId(estimator?.estimatorId);
      setDbData({ ...dbData, day: date, estimatorId: estimator?.estimatorId });
      setSelectTimeOptions(estimator?.timeSlots?.length ? estimator.timeSlots.map((item) => {
        if (item.isAvailable) {
          return {
            value: { timeFrom: item.timeFrom, timeTo: item.timeTo, slotId: item.slot },
            label: convertToTimeStringTimeOfDay(item.timeFrom),
            selectedLabel: convertToTimeStringTimeOfDay(item.timeFrom),
          };
        } return false;
      }) : []);
    }
  };

  const bookAppointment = () => {
    const policies = window.sessionStorage.getItem('policyDetails');
    const policyDetails = policies && JSON.parse(policies).length ? JSON.parse(window.sessionStorage.getItem('policyDetails'))[0] : null;
    const emailAddress = policyDetails?.insured?.email;
    if (selectedTime?.slotId) {
      const searchCriteria = {
        slot: selectedTime.slotId,
        estimatorId,
        audatargetClaimId,
        gwclaimId,
        emailAddress,
        vendorSelection,
        selectedDay,
      };
      return searchCriteria;
    } else {
        const searchCriteria = {
          slot: null,
          estimatorId,
          audatargetClaimId,
          gwclaimId,
          emailAddress,
          vendorSelection,
          selectedDay,
        };
        return searchCriteria;
    }
  };

  useEffect(() => {
    switch (step) {
      case 'cerfLanding':
        updateCurrentPage(messages.landingHeading, 5);
        break;
      case 'selectRepairShop':
        updateCurrentPage(messages.selectRepairShop, 6);
        break;
      case 'chooseAppointment':
        updateCurrentPage(messages.chooseAppointment, 7);
        const today = new Date();
        const searchCriteria = {
          entityId: vendorSelection.entityId,
          dateFrom: moment(today.setDate(today.getDate() + 1)).format('YYYY-MM-DD'),
          dateTo: moment(today.setDate(today.getDate() + 14)).format('YYYY-MM-DD'),
        };
        if (selectedDay) {
          selectDateAvailibility(selectedDay);
        }
        dispatch(audatargetGetAvailibility(searchCriteria));
        break;
      case 'appointmentDetails':
        updateCurrentPage(messages.appointmentDetails, 8);
        break;
      case 'connectWithUs':
        updateCurrentPage(messages.appointmentDetails, 5);
        break;
      default:
        updateCurrentPage(messages.landingHeading, 5);
    }
    // Scroll to the page top
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }, [step]);

  const fetchAddress = async (address) => {
    const dmtiAddress = await getDmtiAddress(address);
    return dmtiAddress;
  };

  useEffect(() => {
    if (step === 'selectRepairShop') {
      fetchAddress(addressInput);
    }
  }, []);

  const onClickContinue = async (nextPage) => {
    const { entityId } = vendorSelection !== null && vendorSelection;
    let searchCriteria = {};
    switch (nextPage) {
      case 'selectRepairShop':
        getDmtiAddress();
        break;
      case 'chooseAppointment':
        if (!addressInput.value) {
          setRequiredErrorAddress(true);
          return;
        }
        if (!vendorSelection) {
          setRequiredError(true);
          return;
        }
        setSelectedDay(null);
        setSelectedTime(null);
        setSelectDayOptions([]);
        setSelectTimeOptions([]);
        break;
      case 'appointmentDetails':
        if (selectDayOptions.length > 0 && (!selectedTime || !selectedDay)) {
          setRequiredError(true);
          return;
        }
        break;
      case 'serviceReview':
        if (!addressInput.value) {
          setRequiredErrorAddress(true);
          return;
        }
        if (selectedDay && selectedTime) {
          searchCriteria = {
            entityId,
            dateFrom: selectedDay,
            dateTo: selectedDay,
          };
          dispatch(audatargetGetAvailibility(searchCriteria));
        } else {
          setDbData({ ...dbData, isAudatargetVisible: true, triggerAudatarget: !!vendorSelection});
          setIsUpdateRequired(true);
          onFinalContinue({
            isAudatargetVisible: true,
            triggerAudatarget: !!vendorSelection,
            repairShop: vendorSelection ? vendorSelection?.name : <FormattedMessage id="Claims Professional to help arrange" defaultMessage="Claims team member to help arrange" />,
            address: vendorSelection?.address,
            appointmentDay: selectedDay,
            appointmentTime: selectedTime,
            appointmentAvailability: bookAppointment(),
            convertToTimeString: (time) => convertToTimeString(time),
            previousStep: () => onClickContinue('selectRepairShop'),
          });
        }
        return;
      case 'connectWithUs':
        setLoading(true);
        const available = await checkFive9Availibility();
        setLoading(false);
        if (!available) {
          onFinalContinue({
            isAudatargetVisible: true,
            triggerAudatarget: false,
            repairShop: <FormattedMessage id="Speak with a claims professional" defaultMessage="Speak with a claims team member" />,
            previousStep: () => onClickContinue('selectRepairShop'),
          });
          setDbData({ ...dbData, isAudatargetVisible: true, triggerAudatarget: false });
          setStep('serviceReview');
          setRequiredError(false);
          setRequiredErrorAddress(false);
          return;
        }
        break;
      default:
        break;
    }
    setIsUpdateRequired(true);
    setStep(nextPage);
    setRequiredError(false);
    setRequiredErrorAddress(false);
  };

  useEffect(() => {
    if (isUpdateRequired) {
      updateClaim('audatarget', step, dbData);
      setIsUpdateRequired(false);
    }
  }, [isUpdateRequired]);

  const openChatbot = () => {
    const chatBot = document.getElementById('five9OpenChatButton');
    if (chatBot) {
      // eslint-disable-next-line no-undef
      Five9ChatSetVariable('Chat.AutoTrigger', true);
      chatBot.click();
    }
  };

  const convertToDateString = (date) => new Date(date.replace(/-/g, '/').replace(/T.+/, '')).toLocaleDateString(`${intl.locale}-CA`, { weekday: 'long', month: 'long', day: 'numeric' }).replace(',', '');
  const convertToTimeString = (time) => new Date(`1970-01-01T${time}Z`).toLocaleTimeString('en-CA', { timeZone: 'UTC', hour12: true, hour: 'numeric', minute: 'numeric' })
    .replace('a.m.', 'AM').replace('p.m.', 'PM');
  const convertToTimeStringTimeOfDay = (time) => {
    const formattedTime = convertToTimeString(time);
    if (formattedTime.slice(-2) === 'AM') {
      return `${intl.formatMessage({ id: 'Morning', defaultValue: 'Morning' })} - ${formattedTime}`;
    } if (parseInt(formattedTime.charAt(0), 10) < 5) {
      return `${intl.formatMessage({ id: 'Afternoon', defaultValue: 'Afternoon' })} - ${formattedTime}`;
    }
    return `${intl.formatMessage({ id: 'Evening', defaultValue: 'Evening' })} - ${formattedTime}`;
  };
  return (
    <>
      {/* Intro */}
      {step === 'cerfLanding' && (
        <Container fluid className="dashboard-content">
          <Grid>
            <Grid.Row>
              <Typography h1 center className="services-heading">
                <FormattedMessage {...messages.landingHeading} />
              </Typography>
            </Grid.Row>
            <div className="services-divider">
              <Grid.Row justifyContentBetween className="reverse-col">
                <Grid.Column colMd8>
                  <div className="services-copy">
                    <p><FormattedMessage {...messages.introCopy} /></p>
                  </div>
                  <div className="services-copy">
                    <strong className="services-copy small-subtitle">
                      <FormattedMessage {...messages.listIntro} />
                    </strong>
                    <ul className="services-list">
                      <li><FormattedMessage {...messages.list1} /></li>
                      <li><FormattedMessage {...messages.list2} /></li>
                      <li><FormattedMessage {...messages.list3} /></li>
                      <li><FormattedMessage {...messages.list4} /></li>
                      <li><FormattedMessage {...messages.list5} /></li>
                    </ul>
                  </div>
                </Grid.Column>
                <Grid.Column colMd3>
                  <div className="right-h">
                    <img src={introIllustration} alt="Car illustration" />
                  </div>
                </Grid.Column>
              </Grid.Row>
            </div>
            <div className="center-v services-btns">
              <Button
                primary
                className="mb-20 services-continue-button"
                onClick={() => onClickContinue('selectRepairShop')}
              ><FormattedMessage {...messages.selectRepairCta} />
              </Button>
              <Button
                onClick={() => onClickContinue('connectWithUs')}
                className="btn save-continue-later"
              >
                <FormattedMessage {...messages.speakToSomeoneCta} />
              </Button>
            </div>
          </Grid>
        </Container>
      )}

      {/* Select repair shop */}
      {step === 'selectRepairShop' && (
        <Container fluid className="dashboard-content">
          <Grid>
            <Grid.Row>
              <Typography h1 center className="services-heading">
                <FormattedMessage {...messages.selectRepairShop} />
              </Typography>
            </Grid.Row>
            <div className="services-divider">
              <Grid.Row justifyContentCenter flex>
                <div className="services-copy">
                  <strong className="services-copy small-subtitle">
                    <FormattedMessage {...messages.howItWorks} />
                  </strong>
                  <ul className="services-list">
                    <li><FormattedMessage {...messages.howItWorks1} /></li>
                    <li><FormattedMessage {...messages.howItWorks2} /></li>
                    <li><FormattedMessage {...messages.howItWorks3} /></li>
                  </ul>
                </div>
                <div className="services-copy">
                  <strong className="services-copy small-subtitle">
                    <FormattedMessage {...messages.shopWill} />
                  </strong>
                  <ul className="services-list">
                    <li><FormattedMessage {...messages.shopWill1} /></li>
                    <li><FormattedMessage {...messages.shopWill2} /></li>
                    <li><FormattedMessage {...messages.shopWill3} /></li>
                    <li><FormattedMessage {...messages.shopWill4} /></li>
                    <li><FormattedMessage {...messages.shopWill5} /></li>
                  </ul>
                </div>
              </Grid.Row>
              <Grid.Row>
                <div className="text-input-width">
                  <CustomAutocompleteDMTI
                    index={0}
                    getCoordinates
                    name="address"
                    label={intl.formatMessage({ id: messages.locationNearMe.id, defaultValue: messages.locationNearMe.defaultMessage })}
                    className="label-question"
                    id="location_typeahead"
                    placeholder={messages.addressPlaceholder.defaultMessage}
                    value={addressInput}
                    customOnChange={(value) => {
                      if (requiredErrorAddress) {
                        setRequiredErrorAddress(value === '');
                      }
                      setAddressInput(value);
                      setVendorSelection(null);
                      setVendorLocatorSearchLocations([]);
                    }}
                    handleChange={(searchedAddress) => getLocations(searchedAddress)}
                  />
                </div>
                {requiredErrorAddress && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
              </Grid.Row>
            </div>
            {Object.keys(userLocation).length > 0 && vendorLocatorSearchLocations.length > 0 && (
              <div className="services-divider">
                <Grid.Row justifyContentCenter flex className="services-copy">
                  <strong className="mb-18 small-subtitle"><FormattedMessage {...messages.chooseShop} /></strong>
                  {
                    vendorLocatorSearchLocations.slice(0, 4).map((item) => (
                      <>
                        <RadioButton
                          secondary
                          key={`vendor-location-${Math.random() * 0xffffffffffff}`}
                          id={`id_vendor-locator-branch-${Math.random() * 0xffffff}`}
                          value={JSON.stringify(item)}
                          name="repair-shop-location"
                          onChange={(value) => {
                            setRequiredError(false);
                            setVendorSelection(JSON.parse(value));
                            setSelectedDay(null);
                            setSelectedTime(null);
                            setSelectDayOptions([]);
                            setSelectTimeOptions([]);
                            setDbData({ ...dbData, vendorLocation: JSON.parse(value) });
                          }}
                          checked={vendorSelection?.entityId === item.entityId}
                          className="services-radio"
                        >
                          <span className="radio-text">{item.name}</span>
                        </RadioButton>
                        {item.address && (
                          <p key={`vendor-location-address-${Math.random() * 0xffffffffffff}`} className="address-text">
                            {item.address?.addressLine1} {item.address?.city}, {item.address?.province} {item.address?.postalCode}
                          </p>
                        )}
                      </>
                    ))
                  }
                  {requiredError && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
                </Grid.Row>
              </div>
            )}
            {Object.keys(userLocation).length > 0 && vendorLocatorSearchLocations.length <= 0 && (
              <div className="services-divider">
                <Grid.Row justifyContentCenter flex className="services-copy">
                  <strong className="mb-18"><FormattedMessage {...messages.chooseShop} /></strong>
                  <div>
                    <FormattedMessage {...messages.noLocationsError1} />
                    <a style={{ textDecoration: 'none' }} href={`tel:${intl.formatMessage(messages.noLocationsError2)}`}><FormattedMessage {...messages.noLocationsError2} /></a>
                    <FormattedMessage {...messages.noLocationsError3} />
                  </div>
                </Grid.Row>
              </div>
            )}

            <Grid.Row justifyContentCenter flex>
              <div className="center-v services-btns">
                <Button
                  primary
                  className="mb-20 services-continue-button"
                  onClick={() => onClickContinue(Object.keys(userLocation).length > 0 && vendorLocatorSearchLocations.length > 0 ? 'chooseAppointment' : 'serviceReview')}
                ><FormattedMessage {...messages.continueCta} />
                </Button>
                <Button
                  className="btn save-continue-later"
                  onClick={() => saveAndContinueLater('audatarget', step, dbData)}
                >
                  <FormattedMessage {...messages.saveContinueCta} />
                </Button>
              </div>
            </Grid.Row>
          </Grid>
        </Container>
      )}

      {/* Choose Appointment */}
      {step === 'chooseAppointment' && (
        <Container fluid className="dashboard-content">
          <Grid>
            <Grid.Row>
              <Typography h1 center className="services-heading">
                <FormattedMessage {...messages.chooseAppointment} />
              </Typography>
            </Grid.Row>
            <div className="services-divider services-copy">
              <Grid.Row justifyContentCenter flex>
                <div className="services-copy xs-subtitle">
                  <FormattedMessage {...messages.youveSelectedShop} />
                </div>
              </Grid.Row>
              <Grid.Row justifyContentCenter flex>
                {vendorSelection?.name && (
                  <strong className="services-copy strong">
                    {vendorSelection.name}
                  </strong>
                )}
                {vendorSelection?.address && (
                  <p className="services-copy">
                    {vendorSelection?.address?.addressLine1} {vendorSelection?.address?.province} {vendorSelection?.address?.postalCode}
                  </p>
                )}
                {vendorSelection?.email && (
                  <span className="services-copy">
                    {vendorSelection.email}
                  </span>
                )}
                {vendorSelection?.number && (
                  <span className="services-copy">
                    {vendorSelection.number}
                  </span>
                )}
              </Grid.Row>
              {vendorSelection?.mapAddress && (
                <div className="ml-8">
                  <Grid.Row>
                    <Button primary underline link href={vendorSelection.mapAddress} rel="noreferrer" target="_blank" className="view-map-button" style={{ color: '#147582' }} newTab>
                      <FormattedMessage {...messages.viewMap} />
                    </Button>
                  </Grid.Row>
                </div>
              )}
            </div>
            {selectDayOptions.length > 0 ? (
              <div className="services-divider services-copy">
                <div className="mb-small-26">
                  <Grid.Row justifyContentCenter flex>
                    <strong className="label">
                      <FormattedMessage {...messages.selectDay} />
                    </strong>
                    <Dropdown
                      primary
                      options={selectDayOptions}
                      placeholder={intl.formatMessage(messages.dropdownPlaceholder)}
                      onChange={(v) => {
                        setSelectedDay(v);
                        setSelectedTime(null);
                        selectDateAvailibility(v);
                      }}
                      value={selectedDay}
                      id="dropdown_day"
                    />
                    {requiredError && !selectedDay && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
                  </Grid.Row>
                </div>
                {(selectTimeOptions.length > 0 || selectedTime) && (
                  <Grid.Row justifyContentCenter flex>
                    <strong className="services-copy">
                      <FormattedMessage {...messages.selectTime} />
                    </strong>
                    <Dropdown
                      primary
                      options={selectTimeOptions.filter((item) => item !== false)}
                      placeholder={`${selectTimeOptions.filter((item) => item !== false).length} ${intl.formatMessage(messages.timesPlaceholder)}`}
                      onChange={(v) => {
                        setSelectedTime(v);
                        setDbData({ ...dbData, time: v });
                      }}
                      value={selectedTime}
                      id="dropdown_time"
                    />
                    {requiredError && !selectedTime && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
                  </Grid.Row>
                )}
              </div>
            ) : (
              <div className="services-divider">
                <Grid.Row justifyContentCenter flex>
                  <strong className="services-copy">
                    <FormattedMessage {...messages.noDatesAvailableTitle} />
                  </strong>
                  <span className="services-copy">
                    <FormattedMessage {...messages.noDatesAvailableText} />
                  </span>
                </Grid.Row>
              </div>
            )}

            <Grid.Row justifyContentCenter flex>
              <div className="center-v services-btns">
                <Button
                  primary
                  className="mb-20 services-continue-button"
                  onClick={() => onClickContinue('appointmentDetails')}
                ><FormattedMessage {...messages.continueCta} />
                </Button>
                <Button
                  secondary
                  className="mb-20 services-continue-button"
                  onClick={() => onClickContinue('selectRepairShop')}
                ><FormattedMessage {...messages.pickShopCta} />
                </Button>
                <Button
                  className="btn save-continue-later"
                  onClick={() => saveAndContinueLater('audatarget', step, dbData)}
                >
                  <FormattedMessage {...messages.saveContinueCta} />
                </Button>
              </div>
            </Grid.Row>
          </Grid>
        </Container>
      )}

      {/* step appointmentDetails */}
      {step === 'appointmentDetails' && (
        <Container fluid className="dashboard-content">
          <Grid>
            <Grid.Row>
              <Typography h1 center className="services-heading">
                <FormattedMessage {...messages.appointmentDetails} />
              </Typography>
            </Grid.Row>
            {selectDayOptions.length > 0 ? (
              <div className="services-divider services-copy">
                <Grid.Row justifyContentCenter flex>
                  <strong className="services-copy xs-subtitle">
                    <FormattedMessage {...messages.initialBook} />
                  </strong>
                </Grid.Row>
                <Grid.Row justifyContentCenter flex>
                  {vendorSelection?.name && (
                    <strong className="services-copy">
                      {vendorSelection.name}
                    </strong>
                  )}
                  {vendorSelection?.address && (
                    <span className="services-copy">
                      {vendorSelection.address?.addressLine1} {vendorSelection.address?.province} {vendorSelection.address?.postalCode}
                    </span>
                  )}
                  {vendorSelection?.email && (
                    <span className="services-copy">
                      {vendorSelection.email}
                    </span>
                  )}
                  {vendorSelection?.number && (
                    <span className="services-copy">
                      {vendorSelection.number}
                    </span>
                  )}
                  {vendorSelection?.mapAddress && (
                    <span className="services-copy">
                      <Button primary underline link href={vendorSelection.mapAddress} rel="noreferrer" target="_blank" className="view-map-button" style={{ color: '#147582' }} newTab>
                        <FormattedMessage {...messages.viewMap} />
                      </Button>
                    </span>
                  )}
                </Grid.Row>
                <Grid.Row justifyContentCenter flex>
                  <strong className="services-copy">
                    {convertToDateString(selectedDay)}
                  </strong>
                  <span className="services-copy">
                    {convertToTimeString(selectedTime.timeFrom)}
                  </span>
                </Grid.Row>
              </div>
            ) : (
              <div className="services-divider">
                <Grid.Row justifyContentCenter flex>
                  <span className="services-copy">
                    <FormattedMessage {...messages.initialNoBook} />
                  </span>
                  <strong className="services-copy">
                    <FormattedMessage {...messages.reachOutTo} />
                  </strong>
                </Grid.Row>
                <Grid.Row justifyContentCenter flex>
                  {vendorSelection?.name && (
                    <strong className="services-copy">
                      {vendorSelection.name}
                    </strong>
                  )}
                  {vendorSelection?.address && (
                    <span className="services-copy">
                      {vendorSelection.address.addressLine1} {vendorSelection.address.province} {vendorSelection.address.postalCode}
                    </span>
                  )}
                  {vendorSelection?.email && (
                    <span className="services-copy">
                      {vendorSelection.email}
                    </span>
                  )}
                  {vendorSelection?.number && (
                    <span className="services-copy">
                      {vendorSelection.number}
                    </span>
                  )}
                  {vendorSelection?.mapAddress && (
                    <span className="services-copy">
                      <Button primary underline link href={vendorSelection.mapAddress} rel="noreferrer" target="_blank" className="view-map-button" newTab style={{ color: '#147582' }}>
                        <FormattedMessage {...messages.viewMap} />
                      </Button>
                    </span>
                  )}
                </Grid.Row>
              </div>
            )}

            <Grid.Row justifyContentCenter flex>
              <div className="center-v services-btns">
                <Button
                  primary
                  className="mb-20 services-continue-button"
                  onClick={() => onClickContinue('serviceReview')}
                ><FormattedMessage {...messages.continueCta} />
                </Button>
                <Button
                  className="btn save-continue-later"
                  onClick={() => saveAndContinueLater('audatarget', step, dbData)}
                >
                  <FormattedMessage {...messages.saveContinueCta} />
                </Button>
              </div>
            </Grid.Row>
          </Grid>
        </Container>
      )}

      {/* Connect with us */}
      {step === 'connectWithUs' && (
        <div className="dashboard-content">
          <div className="wizard-page">
            <Grid.Row>
              <Typography h1 center className="services-heading">
                <FormattedMessage {...messages.connectWithUs} />
              </Typography>
            </Grid.Row>
            <div className="claim-divider mb-30 formio-component formio-component-panel formio-component-connectWithUs section formio-component-label-hidden">
              <div className="services-copy">
                <strong className="services-copy">
                  <FormattedMessage {...messages.lastStep} />
                </strong>
              </div>
              <div className="services-copy mb-small-26">
                <span className="services-copy">
                  <FormattedMessage {...messages.draftSavedMsg} />
                </span>
              </div>
              <Grid.Row justifyContentCenter flex>
                <div className="services-copy">
                  <strong className="services-copy">
                    <FormattedMessage {...messages.chat} />
                  </strong>
                </div>
              </Grid.Row>
              <Grid.Row justifyContentCenter flex>
                <div className="services-copy mb-small-26">
                  <FormattedMessage {...messages.chatDescription1} />
                  <strong className="services-copy">
                    <FormattedMessage {...messages.chatDescriptionBold} />
                  </strong>
                  <FormattedMessage {...messages.chatDescription2} />
                </div>
                <div className="services-copy">
                  <strong className="services-copy">
                    <FormattedMessage {...messages.call} />
                  </strong>
                  <br />
                  <span className="services-copy">
                    <FormattedMessage {...messages.call1} />
                    <a style={{ textDecoration: 'none' }} href={`tel:${intl.formatMessage(messages.call2)}`}><FormattedMessage {...messages.call2} /></a>
                    <FormattedMessage {...messages.call3} />
                  </span>
                </div>
              </Grid.Row>
            </div>

            <Grid.Row justifyContentCenter flex>
              <div className="center-v services-btns">
                <Button
                  primary
                  className="mb-20 services-continue-button"
                  onClick={() => openChatbot()}
                ><FormattedMessage {...messages.startChatBtn} />
                </Button>
                <Button
                  secondary
                  className="mb-20 services-continue-button"
                  onClick={() => onClickContinue('selectRepairShop')}
                ><FormattedMessage {...messages.selectRepairCta} />
                </Button>
                <Button
                  className="btn save-continue-later"
                  onClick={() => saveAndContinueLater('audatarget', step, dbData)}
                >
                  <FormattedMessage {...messages.saveContinueCta} />
                </Button>
              </div>
            </Grid.Row>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <LoadingIndicators.QuoteLoading loading />
        </div>
      )}

      {/* Appointment no longer available Pop Up */}
      <NoLongerAvailablePopUp
        open={noLongerAvailable}
        closePopUp={() => { setNoLongerAvailable(!noLongerAvailable); onClickContinue('chooseAppointment'); }}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  audatargetLocations: audatargetSelector(state.services),
});

export default injectIntl(connect(mapStateToProps)(VendorLocator));
